import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const BG_IMG = "/login-bg.jpg";

export default function Login() {
  const { login: ctxLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // 'password' | '2fa'
  const [pendingToken, setPendingToken] = useState("");
  const [code, setCode] = useState("");

  const submitPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.needs_2fa) {
        setPendingToken(data.pending_token);
        setStep("2fa");
        toast.info("Inserisci il codice della tua app autenticatore");
      } else {
        localStorage.setItem("segretaria_token", data.token);
        // refresh auth context by calling /me
        window.location.href = "/";
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/verify-2fa", {
        pending_token: pendingToken,
        code: code,
      });
      localStorage.setItem("segretaria_token", data.token);
      window.location.href = "/";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* LEFT VISUAL */}
      {/* LEFT VISUAL — full brochure visible (contain, no crop) */}
      <div
        className="hidden lg:flex items-center justify-center relative bg-[#F6F1E8]"
        data-testid="login-bg"
      >
        <img
          src={BG_IMG}
          alt="AB Home Venezia — La tua casa, il nostro impegno"
          className="max-w-full max-h-screen w-auto h-auto object-contain"
        />
      </div>


      {/* RIGHT FORM */}
      <div className="flex items-center justify-center p-8 bg-[#F9F8F6]">
        <div className="w-full max-w-md fade-up">
          {/* Mobile-only logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/logo-abhome.png" alt="AB Home" className="w-24 h-24 object-contain" />
          </div>

          {step === "password" && (
            <>
              <div className="label-mono">Accesso riservato</div>
              <h2 className="font-display text-4xl mt-2 font-light">
                Bentornato.
              </h2>
              <p className="text-sm text-[#5C5B5A] mt-3">
                Inserisci le credenziali per gestire la Segretaria AI di AB Home Venezia.
              </p>

              <form onSubmit={submitPassword} className="mt-10 space-y-5">
                <div>
                  <Label htmlFor="email" className="label-mono">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                    placeholder="manuel@abhomevenezia.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="label-mono">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase tracking-[0.2em] text-xs py-6"
                >
                  {loading ? "Accesso in corso…" : "Accedi"}
                </Button>

                <div className="text-center pt-1">
                  <Link
                    to="/forgot-password"
                    data-testid="login-forgot-password-link"
                    className="text-xs text-[#5C5B5A] hover:text-[#2C4C3B] font-mono uppercase tracking-[0.15em] underline underline-offset-4"
                  >
                    Password dimenticata?
                  </Link>
                </div>

                <p className="text-xs text-center text-[#5C5B5A] pt-2">
                  Primo accesso?{" "}
                  <Link
                    to="/register"
                    data-testid="login-register-link"
                    className="text-[#2C4C3B] hover:text-[#1E3629] underline underline-offset-4 font-mono uppercase tracking-[0.15em]"
                  >
                    Registra il tuo account
                  </Link>
                </p>
              </form>
            </>
          )}

          {step === "2fa" && (
            <>
              <button
                type="button"
                onClick={() => { setStep("password"); setCode(""); }}
                className="text-xs text-[#5C5B5A] hover:text-[#1C1B1A] flex items-center gap-1 mb-6"
                data-testid="login-back-button"
              >
                <ArrowLeft size={12} /> Indietro
              </button>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={20} className="text-[#2C4C3B]" />
                <div className="label-mono">Verifica 2 fattori</div>
              </div>
              <h2 className="font-display text-4xl font-light">Codice autenticatore</h2>
              <p className="text-sm text-[#5C5B5A] mt-3">
                Apri Google Authenticator (o Authy, 1Password) e inserisci il codice di 6 cifre.
              </p>

              <form onSubmit={submit2FA} className="mt-10 space-y-5">
                <div>
                  <Label className="label-mono">Codice 2FA</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={6}
                    required
                    autoFocus
                    data-testid="login-2fa-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="mt-2 border-[#E5E4E0] bg-transparent focus:border-[#2C4C3B] focus:ring-0 font-mono text-3xl text-center tracking-[0.5em] py-6"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="login-2fa-submit"
                  className="w-full bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-mono uppercase tracking-[0.2em] text-xs py-6"
                >
                  {loading ? "Verifica…" : "Verifica codice"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
